import { type JSX, useCallback, useEffect, useRef } from 'react';

import { type Content, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Color from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';

import { alertDialog, promptDialog } from '../../components/dialogs.js';

export interface RichTextEditorProps {
  /**
   * Current ProseMirror JSON serialized as a string. The editor parses
   * this on mount + when the prop changes, and emits a new serialized
   * string via onChange whenever the user types.
   */
  contentJson: string;
  onChange: (nextJson: string) => void;
}

/**
 * Tiptap-backed WYSIWYG editor for the richText hero. Tiptap is the
 * authoring layer only — the launcher renderer uses an in-house
 * ProseMirror-JSON walker (HeroRichText.tsx) so it doesn't carry the
 * Tiptap dependency at runtime. Extensions here mirror the renderer's
 * whitelist 1:1 (StarterKit + Link with safe-protocol restriction)
 * so anything the user can author is also something the renderer can
 * paint — no silent feature drift between Builder and launcher.
 *
 * The serialized JSON is debounced via tiptap's `onUpdate`, which
 * itself batches per keystroke; the Builder's store layer then
 * coalesces upstream postMessage to the preview iframe.
 */
export function RichTextEditor({
  contentJson,
  onChange,
}: RichTextEditorProps): JSX.Element {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        // Match the renderer-side whitelist — http/https/mailto only.
        // Tiptap calls validate() during paste/insert; refusing other
        // protocols here means the JSON never gets a hostile link mark
        // baked in.
        validate: (href) =>
          href.startsWith('http://') ||
          href.startsWith('https://') ||
          href.startsWith('mailto:'),
      }),
      // Image insert — captured as a base64 data URL so the launcher
      // can ship the image inline with the config (no separate asset
      // pipeline). The handler below caps file size + only accepts
      // image/*; the renderer's HeroRichText walks the `image` node
      // and emits a safe <img> with the data URL.
      Image.configure({ inline: false, allowBase64: true }),
      // Per-node text alignment for paragraphs + headings. Stored as
      // an attribute (`textAlign: 'left'|'center'|'right'`) so the
      // renderer can paint it via a style attr.
      TextAlign.configure({ types: ['paragraph', 'heading'] }),
      // TextStyle is the base mark Color extends; Color stores a hex
      // string in the JSON. Renderer accepts only 3-/6-digit hex
      // (see HeroRichText safe-color check) so a hostile payload
      // can't sneak CSS injection in via colour.
      TextStyle,
      Color,
    ],
    content: safeParseJson(contentJson) as Content,
    onUpdate({ editor: ed }) {
      onChange(JSON.stringify(ed.getJSON()));
    },
    editorProps: {
      attributes: {
        class: 'richtext-editor__content',
        spellcheck: 'true',
      },
    },
  });

  // Keep the editor in sync if the prop changes from outside (e.g.,
  // the user switched to a different profile or reset the theme).
  // Comparing JSON strings is fine — tiptap's getJSON is deterministic.
  useEffect(() => {
    if (editor === null) return;
    const currentJson = JSON.stringify(editor.getJSON());
    if (currentJson === contentJson) return;
    editor.commands.setContent(safeParseJson(contentJson) as Content);
  }, [editor, contentJson]);

  // Toolbar handlers — small, declarative; keyboard shortcuts (Cmd+B
  // / Cmd+I / etc.) are provided by tiptap's StarterKit so the toolbar
  // doesn't need to handle those.
  const toggleBold = useCallback(() => editor?.chain().focus().toggleBold().run(), [editor]);
  const toggleItalic = useCallback(() => editor?.chain().focus().toggleItalic().run(), [editor]);
  const toggleStrike = useCallback(() => editor?.chain().focus().toggleStrike().run(), [editor]);
  const toggleCode = useCallback(() => editor?.chain().focus().toggleCode().run(), [editor]);
  const toggleBulletList = useCallback(
    () => editor?.chain().focus().toggleBulletList().run(),
    [editor],
  );
  const toggleOrderedList = useCallback(
    () => editor?.chain().focus().toggleOrderedList().run(),
    [editor],
  );
  const toggleBlockquote = useCallback(
    () => editor?.chain().focus().toggleBlockquote().run(),
    [editor],
  );
  const toggleH2 = useCallback(
    () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
    [editor],
  );
  const toggleH3 = useCallback(
    () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
    [editor],
  );
  const addLink = useCallback(() => {
    if (editor === null) return;
    // Prefill the dialog with the current link href when one is active,
    // so users editing an existing link don't have to retype.
    const prev = editor.getAttributes('link')['href'] as string | undefined;
    void (async () => {
      const next = await promptDialog({
        title: 'Link',
        message: 'Link URL (https://, http://, or mailto:)',
        defaultValue: prev ?? '',
        confirmLabel: 'Apply',
      });
      if (next === null) return;
      if (next === '') {
        editor.chain().focus().unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: next }).run();
    })();
  }, [editor]);

  const alignLeft = useCallback(
    () => editor?.chain().focus().setTextAlign('left').run(),
    [editor],
  );
  const alignCenter = useCallback(
    () => editor?.chain().focus().setTextAlign('center').run(),
    [editor],
  );
  const alignRight = useCallback(
    () => editor?.chain().focus().setTextAlign('right').run(),
    [editor],
  );

  const setColor = useCallback(
    (color: string) => editor?.chain().focus().setColor(color).run(),
    [editor],
  );
  const unsetColor = useCallback(
    () => editor?.chain().focus().unsetColor().run(),
    [editor],
  );

  /**
   * Image insert — opens a file picker, reads the chosen image as a
   * base64 data URL, and emits an `image` node. Capped at ~1 MB
   * encoded so the config blob doesn't bloat with a 5 MB photo.
   */
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const onPickImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const onImageFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ''; // allow re-picking the same file
      if (!file || editor === null) return;
      if (!file.type.startsWith('image/')) return;
      if (file.size > 1_000_000) {
        void alertDialog({
          title: 'Image too large',
          message: 'Image is too large — pick one under 1 MB.',
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') return;
        editor.chain().focus().setImage({ src: result }).run();
      };
      reader.readAsDataURL(file);
    },
    [editor],
  );

  if (editor === null) {
    return <div className="richtext-editor richtext-editor--loading">Loading editor…</div>;
  }

  return (
    <div className="richtext-editor" data-testid="richtext-editor">
      <div className="richtext-editor__toolbar" role="toolbar" aria-label="Formatting">
        <ToolbarBtn active={editor.isActive('bold')} onClick={toggleBold} title="Bold (⌘B)">
          <b>B</b>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('italic')} onClick={toggleItalic} title="Italic (⌘I)">
          <i>I</i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('strike')} onClick={toggleStrike} title="Strikethrough">
          <s>S</s>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('code')} onClick={toggleCode} title="Inline code">
          {'<>'}
        </ToolbarBtn>
        <span className="richtext-editor__sep" aria-hidden />
        <ToolbarBtn
          active={editor.isActive('heading', { level: 2 })}
          onClick={toggleH2}
          title="Heading"
        >
          H2
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('heading', { level: 3 })}
          onClick={toggleH3}
          title="Subheading"
        >
          H3
        </ToolbarBtn>
        <span className="richtext-editor__sep" aria-hidden />
        <ToolbarBtn
          active={editor.isActive('bulletList')}
          onClick={toggleBulletList}
          title="Bulleted list"
        >
          •
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('orderedList')}
          onClick={toggleOrderedList}
          title="Numbered list"
        >
          1.
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('blockquote')}
          onClick={toggleBlockquote}
          title="Quote"
        >
          ❝
        </ToolbarBtn>
        <span className="richtext-editor__sep" aria-hidden />
        <ToolbarBtn active={editor.isActive('link')} onClick={addLink} title="Link">
          🔗
        </ToolbarBtn>
        <ToolbarBtn active={false} onClick={onPickImage} title="Insert image">
          🖼
        </ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onImageFile}
          style={{ display: 'none' }}
          data-testid="richtext-image-input"
        />
        <span className="richtext-editor__sep" aria-hidden />
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'left' })}
          onClick={alignLeft}
          title="Align left"
        >
          ⇤
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'center' })}
          onClick={alignCenter}
          title="Align center"
        >
          ↔
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'right' })}
          onClick={alignRight}
          title="Align right"
        >
          ⇥
        </ToolbarBtn>
        <span className="richtext-editor__sep" aria-hidden />
        <label
          className="richtext-editor__color"
          title="Text color"
        >
          <span aria-hidden>🎨</span>
          <input
            type="color"
            value={(editor.getAttributes('textStyle')['color'] as string | undefined) ?? '#e8eaed'}
            onChange={(e) => setColor(e.target.value)}
            className="richtext-editor__color-input"
            data-testid="richtext-color-input"
          />
        </label>
        <ToolbarBtn
          active={false}
          onClick={unsetColor}
          title="Clear color"
        >
          ✕
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarBtn({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`richtext-editor__btn${active ? ' richtext-editor__btn--active' : ''}`}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

/**
 * Tolerant JSON parse — Tiptap's `setContent` / `content` props accept
 * any JSON shape and will surface an internal warning on a malformed
 * doc; passing a known-good empty doc when parsing fails keeps the
 * editor mountable. Returns `unknown` so the tiptap call sites cast
 * to the library's `Content` shape without us re-declaring it.
 */
function safeParseJson(s: string): unknown {
  const empty = { type: 'doc', content: [] };
  try {
    const parsed = JSON.parse(s) as unknown;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'type' in parsed &&
      (parsed as { type: unknown }).type === 'doc'
    ) {
      return parsed;
    }
    return empty;
  } catch {
    return empty;
  }
}
